import React, { useEffect } from 'react'
import * as R from 'ramda'
import { utils } from '@ims/1edtech-frontend-common'

import { useSelector } from 'react-redux'
import { navigationParamSelector } from 'lib/utils/navigation'

import { Spinner, Paper, Screen, Linkify, Button, Icon } from 'lib/components'
import { DocumentTitle } from 'lib/hooks/useDocumentTitle'
import BaseTrustedAppsBreadCrumbs from 'domains/trustedApps/components/BaseTrustedAppsBreadCrumbs'
import TitleBar from 'domains/trustedApps/components/TrustedAppDetails/TitleBar'
import HTML from 'lib/components/HTML'
import TrustedAppDetailLink from 'domains/trustedApps/components/TrustedAppDetails/TrustedAppDetailLink'
import CertifiedIcon from 'domains/trustedApps/components/icons/CertifiedIcon'
import VettedIcon from 'domains/trustedApps/components/icons/VettedIcon'
import { DISTRICT_TRUSTED_APPS_ROUTE } from 'domains/application/navigation/routes'
import IIMSApplication, {
  isIMSAppCertified,
  isIMSAppVetted,
} from 'domains/trustedApps/models/IIMSApplication'
import useTrustedApp from 'domains/trustedApps/hooks/useTrustedApp'
import useModalState from 'lib/hooks/useModalState'
import InfoCardItems from 'lib/components/InfoCard/InfoCardItems'
import WidgetHeader from 'domains/dashboard/components/WidgetHeader'
import EditAdditionalInfoModal from 'domains/trustedApps/components/EditAdditionalInfoModal'
import InfoCardItem from 'lib/components/InfoCard/InfoCardItem'
import {
  hasAdditionalInfo,
  TrustedAppRating,
} from 'domains/trustedApps/models/ITrustedApplication'
import { TRUSTED_APPS_RATINGS_RECORD } from 'lib/records/modules/trustedAppsRatings'
import useRecordEntity from 'lib/records/hooks/useRecordEntity'
import { getRecord } from 'lib/records/workflows/getRecord'
import { TRUSTED_APPS_RECORD } from 'lib/records/modules/trustedApplications'
import { TrustedAppSurveyResponse } from '../models/ITrustedAppsCustomQuestions'

function Link(props: { label: string; link?: string }) {
  if (!utils.hasValue(props.link)) {
    return null
  }

  return (
    <p className="text-base text-text mb-3">
      <span className="font-bold">{props.label}</span>{' '}
      <Linkify>{props.link || ''}</Linkify>
    </p>
  )
}

export default function TrustedAppDetailsScreen(props: any) {
  const id = utils.convertToInt(
    useSelector((state: any) => navigationParamSelector('id', 0)(state, props)),
  )

  const [, imsAppDetails, selectedAppDetails, pending, parentOrgId] =
    useTrustedApp(props)
  const [editModalIsOpen, openEditModal, closeEditModal] = useModalState()

  const [ratingsResponse] = useRecordEntity({
    record: TRUSTED_APPS_RATINGS_RECORD,
    id: 1,
    noReFetch: true,
  })

  useEffect(() => {
    const refreshAppDetails = async () => {
      try {
        await getRecord(
          TRUSTED_APPS_RECORD,
          id,
          parentOrgId ? { parentRecordId: parentOrgId } : {},
        )
      } catch (error) {
        console.error(error) //eslint-disable-line
      }
    }

    refreshAppDetails()
  }, []) //eslint-disable-line

  if (pending || (!imsAppDetails && !parentOrgId)) {
    return (
      <Screen>
        <DocumentTitle title="Application Details" />
        <BaseTrustedAppsBreadCrumbs {...props} crumbs={[{ name: '...' }]} />

        <Spinner centered={true} />
      </Screen>
    )
  }

  const imsApp =
    imsAppDetails ||
    ((selectedAppDetails as any).application as IIMSApplication)
  const title = R.pathOr('', ['productName'], imsApp)

  const hasInfo = hasAdditionalInfo({ ...(selectedAppDetails || {}) } as any)

  const additionInfoItems =
    selectedAppDetails && selectedAppDetails.surveyResults
      ? selectedAppDetails?.surveyResults
          ?.sort((a, b) => a.questionText.localeCompare(b.questionText))
          .map((item: any) => ({
            label: item.questionText,
            value: item.responses
              .map(
                (response: TrustedAppSurveyResponse) => response.responseText,
              )
              .sort((a: string, b: string) => a.localeCompare(b))
              .join(', '),
          }))
      : []

  const tags = selectedAppDetails?.tags || []

  const hasTags = tags.length > 0
  const hasQuestions = additionInfoItems.length > 0
  const hasLicensingFees = !!selectedAppDetails?.licensingFees
  const hasLicensingExpirationDate =
    !!selectedAppDetails?.licensingExpirationDate
  const hasTechnicalNotes = !!selectedAppDetails?.technicalNotes
  const hasInstructionalNotes = !!selectedAppDetails?.instructionalNotes

  const hasStaticInfo =
    hasLicensingFees ||
    hasLicensingExpirationDate ||
    hasTechnicalNotes ||
    hasInstructionalNotes

  const isEmpty = !hasTags && !hasQuestions && !hasStaticInfo

  let rating: TrustedAppRating | undefined =
    selectedAppDetails?.rating || undefined

  if (!rating?.ratingText) {
    if (ratingsResponse?.ratings) {
      rating = R.find(
        (rating) => rating.ratingId === selectedAppDetails?.rating?.ratingId,
        ratingsResponse.ratings,
      )
    }
  }

  return (
    <Screen>
      <DocumentTitle title={title} />
      <BaseTrustedAppsBreadCrumbs {...props} crumbs={[{ name: title }]} />

      <Paper>
        <TitleBar app={imsApp} rating={rating} />

        <div className="mt-4">
          {utils.hasValue(imsApp.productDescription) && (
            <div data-test="trusted-app-description">
              <HTML fontSize={16}>{imsApp.productDescription}</HTML>
            </div>
          )}

          <div className="mt-4" data-test="trusted-app-orgUrl">
            <Link label="Org Url" link={imsApp.orgExternalUrl} />
          </div>

          <div className="mt-4 md:grid grid-cols-2 gap-3">
            <TrustedAppDetailLink
              title="View Certifications"
              icon={<CertifiedIcon width={40} height={40} />}
              route={`${DISTRICT_TRUSTED_APPS_ROUTE}${
                parentOrgId ? `/shared/${parentOrgId}` : ''
              }/${imsApp.id}/certifications`}
              enabled={isIMSAppCertified(imsApp)}
            />
            <TrustedAppDetailLink
              title="View App Vetting"
              icon={<VettedIcon width={40} height={40} />}
              route={`${DISTRICT_TRUSTED_APPS_ROUTE}${
                parentOrgId ? `/shared/${parentOrgId}` : ''
              }/${imsApp.id}/vettings`}
              enabled={isIMSAppVetted(imsApp)}
            />
          </div>
        </div>
      </Paper>

      {!!selectedAppDetails && (
        <Paper mt={4}>
          <div className="flex flex-col gap-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
              <h4 className="font-bold text-xl text-text">Additional Info</h4>
              {!parentOrgId && (
                <Button
                  variant={hasInfo ? 'success' : 'complete'}
                  onClick={openEditModal}
                  data-test="edit-btn"
                >
                  <div className="flex flex-row items-center space-x-2">
                    <Icon className="fas fa-edit" color="white" />
                    <p className="text-white text-sm font-bold">
                      {hasInfo ? 'Edit' : 'Add'} Additional Info
                    </p>
                  </div>
                </Button>
              )}
            </div>

            {isEmpty && (
              <div className="text-center">
                No additional information has been entered for this application.
              </div>
            )}

            {hasStaticInfo && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {!!selectedAppDetails?.technicalNotes && (
                  <InfoCardItem
                    label="Technical Notes"
                    value={selectedAppDetails?.technicalNotes}
                    showIfNoValue
                    html
                    scrollable
                  />
                )}
                {!!selectedAppDetails?.instructionalNotes && (
                  <InfoCardItem
                    label="Instructional Notes"
                    value={selectedAppDetails?.instructionalNotes}
                    showIfNoValue
                    html
                    scrollable
                  />
                )}
                {!!selectedAppDetails.licensingFees && (
                  <InfoCardItem
                    label="Licensing Fees"
                    value={selectedAppDetails.licensingFees}
                  />
                )}
                {!!selectedAppDetails?.licensingExpirationDate && (
                  <InfoCardItem
                    label="Licensing Expiration Date"
                    value={utils.date.formatDate(
                      selectedAppDetails?.licensingExpirationDate,
                    )}
                  />
                )}
              </div>
            )}

            {hasTags && (
              <div className="flex flex-col gap-4">
                <h5 className="text-lg font-bold text-text">
                  Application Tags
                </h5>
                <div className="flex flex-wrap space-x-2">
                  {tags
                    .sort((a: string, b: string) => a.localeCompare(b))
                    .map((tag: string) => (
                      <div
                        key={tag}
                        className="px-4 py-1 bg-silver rounded-full"
                      >
                        {tag}
                      </div>
                    ))}
                </div>
              </div>
            )}

            {hasQuestions && (
              <div className="flex flex-col gap-4">
                <WidgetHeader title="TrustEd App Questions & Answers" />
                <InfoCardItems items={additionInfoItems} showIfNoValue />
              </div>
            )}
          </div>
        </Paper>
      )}

      {!parentOrgId && (
        <EditAdditionalInfoModal
          isOpen={!!selectedAppDetails && editModalIsOpen}
          onRequestClose={closeEditModal}
          app={selectedAppDetails ? selectedAppDetails : undefined}
        />
      )}
    </Screen>
  )
}
